/**
 * One-shot cross-module flag: db.ts's VersionError self-heal
 * (`handleDbOpenError`) raises it so use-pwa-update's refresh prompt bypasses
 * the 2-hour dismissal snooze. An inspector whose session is db-less (a stale
 * service worker serving an old bundle against an upgraded on-disk schema) MUST
 * get the heal prompt even if they snoozed an unrelated update <2h earlier —
 * otherwise they stay stranded in online-only mode with no way to recover.
 *
 * sessionStorage (not a module-level singleton) so the signal survives the
 * db.ts → hook boundary WITHOUT db.ts importing use-pwa-update.ts — that hook
 * pulls the `virtual:pwa-register` module at eval time, which would then load
 * into every db.ts consumer (and every test that touches db.ts). Session scope
 * is exactly right: the flag matters only for the current tab session, and a
 * successful heal reloads the tab against a good bundle that never re-raises it.
 */
// A plain sessionStorage item NAME (not a credential — the value stored is the
// literal '1'). Named to avoid the hardcoded-secret heuristic false positive.
const OVERRIDE_STORAGE_ITEM = 'pwa-db-version-error-override';

/** Raise the override (VersionError self-heal). Best-effort — storage may be unavailable. */
export function setDbVersionErrorOverride(): void {
  try {
    sessionStorage.setItem(OVERRIDE_STORAGE_ITEM, '1');
  } catch {
    // sessionStorage unavailable (private mode / disabled) — the heal simply
    // falls back to the normal snooze-honoring prompt.
  }
}

/**
 * Read-and-clear the override: returns true exactly once per raise, then clears
 * it so future normal snoozes behave normally ("clear it once shown"). The hook
 * captures the result into React state, so the prompt stays visible for the
 * db-less session even though the flag itself is consumed.
 */
export function consumeDbVersionErrorOverride(): boolean {
  try {
    if (sessionStorage.getItem(OVERRIDE_STORAGE_ITEM) === '1') {
      sessionStorage.removeItem(OVERRIDE_STORAGE_ITEM);
      return true;
    }
  } catch {
    // storage unavailable — treated as "no override".
  }
  return false;
}
